// Importing required libraries and types
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Video, VideoState } from '../../types/videoTypes'
import {
  fetchVideosThunk,
  createVideoThunk,
  updateVideoThunk,
  deleteVideoThunk,
} from './videoThunk'

// Initial state setup for video slice
const initialState: VideoState = {
  videos: [],
  currentVideo: null,
  currentIndex: null,
  isLoading: false,
  error: null,
  isPlaying: false,
  fullScreen: false,
}

// Video slice for state management
const videoSlice = createSlice({
  name: 'video',
  initialState,
  reducers: {
    setCurrentVideo: (state, action: PayloadAction<Video>) => {
      state.currentVideo = action.payload
    },
    setIsPlaying: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload
    },
    setFullScreen: (state, action: PayloadAction<boolean>) => {
      state.fullScreen = action.payload
    },
    updateProgress: (
      state,
      action: PayloadAction<{ id: string; progress: number }>
    ) => {
      const { id, progress } = action.payload
      const videoIndex = state.videos.findIndex((video) => video.id === id)
      if (videoIndex !== -1) {
        state.videos[videoIndex].progress = progress
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVideosThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(
        fetchVideosThunk.fulfilled,
        (state, action: PayloadAction<Video[]>) => {
          state.videos = action.payload || []
          state.isLoading = false
        }
      )
      .addCase(fetchVideosThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch videos'
      })
      .addCase(createVideoThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(
        createVideoThunk.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.videos.unshift(action.payload)
          state.isLoading = false
        }
      )
      .addCase(updateVideoThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(
        updateVideoThunk.fulfilled,
        (state, action: PayloadAction<any>) => {
          const updatedVideo = action.payload
          const index = state.videos.findIndex(
            (video) => video.id === updatedVideo.id
          )
          if (index !== -1) {
            state.videos[index] = {
              ...state.videos[index],
              ...updatedVideo,
            }
          }
          state.isLoading = false
        }
      )
      .addCase(deleteVideoThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(
        deleteVideoThunk.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.videos = state.videos.filter(
            (video) => video.id !== action.payload
          )
          state.isLoading = false
        }
      )
      .addCase(deleteVideoThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to delete video'
      })
  },
})

// Export actions and reducer
export const { setCurrentVideo, updateProgress, setIsPlaying, setFullScreen } =
  videoSlice.actions

export default videoSlice.reducer
